// ============================================================================
// Theme Colors
// ============================================================================
// Theme colors are CSS custom properties set on :root by ThemeSync at runtime.
// Default values (navy-green palette) are declared in index.html so the page
// paints correctly before the DB fetch resolves.
// ============================================================================

// ============================================================================
// Colors
// ============================================================================

export const colors = {
  // Theme palette — driven by CSS vars written by ThemeSync
  primary: 'var(--color-primary)',
  primaryDark: 'var(--color-primaryDark)',
  primaryLight: 'var(--color-primaryLight)',
  accent: 'var(--color-accent)',
  accentDark: 'var(--color-accentDark)',
  accentLight: 'var(--color-accentLight)',

  // Grays & Neutrals
  gray: '#696969',
  grayDark: '#2c2c2c',
  grayLight: '#9CA3AF',
  white: '#ffffff',
  textLight: '#ffffffcc',
  textDark: '#000000cc',
  textMuted: '#888888',
  borderLight: '#dddddd',
  borderDark: 'rgba(100, 100, 100, 0.2)',
  backgroundTint: 'rgba(104, 104, 104, 0.02)',
  hoverTint: 'rgba(75, 75, 75, 0.08)',

  // Light Mode
  background: '#fafafa',
  backgroundAlt: '#ebebeb',
  textLightMode: '#1A1A1A',
  textLightModeMuted: '#646464',

  // Dark Mode
  backgroundDarkMode: '#0F0F0F',
  backgroundDarkModeAlt: '#242424',
  textDarkMode: '#F5F5F5',
  textDarkModeMuted: '#A0A0A0',

  // State colors
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
} as const

// ============================================================================
// Gradients
// ============================================================================

export const gradients = {
  primary: 'linear-gradient(135deg, var(--color-primaryDark), var(--color-primary), var(--color-primaryLight))',
  primaryReverse: 'linear-gradient(135deg, var(--color-primaryLight), var(--color-primary), var(--color-primaryDark))',
  accent: 'linear-gradient(135deg, var(--color-accentDark), var(--color-accent), var(--color-accentLight))',
  accentReverse: 'linear-gradient(135deg, var(--color-accentLight), var(--color-accent), var(--color-accentDark))',
  gray: `linear-gradient(135deg, ${colors.grayDark}, ${colors.gray}, ${colors.grayLight})`,
  grayReverse: `linear-gradient(135deg, ${colors.grayLight}, ${colors.gray}, ${colors.grayDark})`,
  primaryAccent: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
  accentPrimary: 'linear-gradient(135deg, var(--color-accent), var(--color-primary))',
} as const

// ============================================================================
// Shadows
// ============================================================================

export const shadows = {
  card: '0 1px 3px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.05)',
  cardMedium: '0 4px 16px rgba(0,0,0,0.10)',
  cardLarge: '0 8px 30px rgba(0,0,0,0.13)',
  primary: '0 2px 12px var(--color-primaryDark)',
  accent: '0 2px 12px var(--color-accentDark)',
  gray: '0 2px 12px rgba(0,0,0,0.18)',
  success: '0 2px 12px rgba(40, 167, 69, 0.25)',
  danger: '0 2px 12px rgba(220, 53, 69, 0.25)',
  warning: '0 2px 12px rgba(255, 193, 7, 0.25)',
  medium: '0 4px 15px var(--color-primaryDark)',
  large: '0 8px 25px var(--color-primaryDark)',
} as const

// ============================================================================
// Design Tokens
// ============================================================================

export const radius = {
  default: '8px',
  large: '12px'
} as const

export const transform = {
  default: 'translateY(-2px)',
  large: 'translateY(-4px)',
} as const

// ============================================================================
// Backward-compat: effects & transitions
// ============================================================================

export const effects = {
  backdropBlur: 'blur(15px)',
  backdropBlurStrong: 'blur(20px)',
  invertDark: 'invert(0)',
} as const

export const transitions = {
  default: 'all 0.3s ease',
  fast: 'all 0.2s ease',
  slow: 'all 0.5s ease',
} as const
